import { formatElplanAlertMessage } from 'api/elplan/elplan.alert.helper'
import type { ElplanTranslate } from 'api/elplan/elplan.alert.types'
import type { RowValues } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import { isDayAheadBidCellFaultStillApplicable } from './DayAheadBidTable.faults.helper'
import { getDayAheadBidCellKey } from './DayAheadBidTable.grid.helper'
import type {
  DayAheadBidCellFault,
  DayAheadBidColumn,
  DayAheadBidRow,
  DayAheadBidSaveRejection,
} from './DayAheadBidTable.types'

const UNPLACED_ROW_LABEL_LIMIT = 3

type DayAheadBidFaultDisplayContext = {
  rows: DayAheadBidRow[]
  rowLabels: string[]
  configuredColumns: DayAheadBidColumn[]
  renderedColumns: DayAheadBidColumn[]
  values: RowValues[]
  translate: ElplanTranslate
}

type ProjectDayAheadBidCellFaultsInput = DayAheadBidFaultDisplayContext & {
  faults: DayAheadBidCellFault[]
  rejectedValues: RowValues[]
}

type DayAheadBidCellFaultProjection = {
  faultMessageByCellKey: ReadonlyMap<string, string>
  unplacedMessages: string[]
}

type DayAheadBidSaveRejectionDescription = {
  faultMessageByCellKey: ReadonlyMap<string, string>
  bannerMessage: string | undefined
}

function describeUnplacedFaults(
  unplaced: { fault: DayAheadBidCellFault; message: string }[],
  {
    configuredColumns,
    rowLabels,
    translate,
  }: Pick<DayAheadBidFaultDisplayContext, 'configuredColumns' | 'rowLabels' | 'translate'>
): string[] {
  const groups = new Map<string, { tagLabel: string; message: string; rowsByIndex: Map<number, string> }>()

  for (const { fault, message } of unplaced) {
    const tagLabel = configuredColumns.find(({ tagId }) => tagId === fault.tagId)?.tagKey ?? String(fault.tagId)
    const groupKey = `${tagLabel}\u0000${message}`
    const group = groups.get(groupKey) ?? { tagLabel, message, rowsByIndex: new Map<number, string>() }
    const { rowIndex } = fault
    const rowLabel = rowIndex === undefined ? undefined : rowLabels[rowIndex]
    if (rowIndex !== undefined && rowLabel !== undefined) {
      group.rowsByIndex.set(rowIndex, rowLabel)
    }
    groups.set(groupKey, group)
  }

  return [...groups.values()].map(({ tagLabel, message, rowsByIndex }) => {
    const headline = translate('{{tagKey}}: {{message}}', { tagKey: tagLabel, message })
    const labels = [...rowsByIndex.entries()].sort(([a], [b]) => a - b).map(([, label]) => label)
    if (labels.length === 0) {
      return headline
    }

    const shown = labels.slice(0, UNPLACED_ROW_LABEL_LIMIT).join(', ')
    const remaining = labels.length - UNPLACED_ROW_LABEL_LIMIT
    return remaining > 0
      ? `${headline} (${shown}, ${translate('+{{count}} more', { count: remaining })})`
      : `${headline} (${shown})`
  })
}

function withTerminalPunctuation(sentence: string): string {
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`
}

function joinFaultSentences(sentences: string[]): string {
  return sentences
    .map((sentence, index) => (index === sentences.length - 1 ? sentence : withTerminalPunctuation(sentence)))
    .join(' ')
}

export function projectDayAheadBidCellFaults(input: ProjectDayAheadBidCellFaultsInput): DayAheadBidCellFaultProjection {
  const { faults, rows, renderedColumns, values, rejectedValues, translate } = input

  const cellMessages = new Map<string, Set<string>>()
  const unplaced: { fault: DayAheadBidCellFault; message: string }[] = []

  for (const fault of faults) {
    if (!isDayAheadBidCellFaultStillApplicable(fault, rows, values, rejectedValues)) {
      continue
    }

    const message = formatElplanAlertMessage(fault.alert, translate)
    if (fault.rowIndex === undefined || !renderedColumns.some(({ tagId }) => tagId === fault.tagId)) {
      unplaced.push({ fault, message })
      continue
    }

    const cellKey = getDayAheadBidCellKey(fault.rowIndex, fault.tagId)
    cellMessages.set(cellKey, (cellMessages.get(cellKey) ?? new Set()).add(message))
  }

  return {
    faultMessageByCellKey: new Map(
      [...cellMessages].map(([cellKey, messages]) => [cellKey, joinFaultSentences([...messages])])
    ),
    unplacedMessages: describeUnplacedFaults(unplaced, input),
  }
}

function getSaveRejectionBannerMessage(
  rejection: DayAheadBidSaveRejection | null,
  unplacedMessages: string[],
  translate: ElplanTranslate
): string | undefined {
  if (rejection === null) {
    return undefined
  }
  if (rejection.faults.length === 0 && rejection.requestAlert !== undefined) {
    return formatElplanAlertMessage(rejection.requestAlert, translate)
  }
  return unplacedMessages.length > 0 ? unplacedMessages.join(' — ') : undefined
}

export function describeDayAheadBidSaveRejection(
  rejection: DayAheadBidSaveRejection | null,
  context: DayAheadBidFaultDisplayContext
): DayAheadBidSaveRejectionDescription {
  const { faultMessageByCellKey, unplacedMessages } = projectDayAheadBidCellFaults({
    ...context,
    faults: rejection?.faults ?? [],
    rejectedValues: rejection?.rejectedValues ?? context.values,
  })

  return {
    faultMessageByCellKey,
    bannerMessage: getSaveRejectionBannerMessage(rejection, unplacedMessages, context.translate),
  }
}

export function describeDayAheadBidSaveRejectionAlert(
  rejection: DayAheadBidSaveRejection,
  context: DayAheadBidFaultDisplayContext
): string {
  const markedCount = describeDayAheadBidSaveRejection(rejection, context).faultMessageByCellKey.size
  return markedCount > 0
    ? context.translate('The table was not saved. {{count}} cells are marked in red.', { count: markedCount })
    : context.translate('The table was not saved.')
}
